import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiTrendingDown, FiCheckCircle, FiAlertTriangle } = FiIcons;

const BMIResults = ({ bmi }) => {
  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600', bg: 'bg-blue-100', icon: FiTrendingUp };
    if (bmi < 25) return { category: 'Normal weight', color: 'text-green-600', bg: 'bg-green-100', icon: FiCheckCircle };
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600', bg: 'bg-yellow-100', icon: FiAlertTriangle };
    return { category: 'Obese', color: 'text-red-600', bg: 'bg-red-100', icon: FiTrendingDown };
  };

  const getHealthTips = (bmi) => {
    if (bmi < 18.5) {
      return [
        'Consider increasing caloric intake with nutrient-dense foods',
        'Focus on strength training to build muscle mass',
        'Consult with a healthcare provider for personalized advice'
      ];
    }
    if (bmi < 25) {
      return [
        'Maintain your current healthy weight through balanced diet',
        'Continue regular physical activity',
        'Focus on overall wellness and healthy habits'
      ];
    }
    if (bmi < 30) {
      return [
        'Consider a moderate calorie reduction (300-500 calories/day)',
        'Increase physical activity to 150+ minutes per week',
        'Focus on whole foods and portion control'
      ];
    }
    return [
      'Consult with a healthcare provider for a comprehensive plan',
      'Consider working with a registered dietitian',
      'Aim for gradual weight loss (1-2 pounds per week)'
    ];
  };

  const { category, color, bg, icon } = getBMICategory(bmi);
  const tips = getHealthTips(bmi);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-2xl shadow-xl p-8"
    >
      <div className="text-center mb-6">
        <div className={`inline-flex items-center justify-center w-16 h-16 ${bg} rounded-full mb-4`}>
          <SafeIcon icon={icon} className={`text-2xl ${color}`} />
        </div>
        <h3 className="text-3xl font-bold text-gray-800 mb-2">{bmi}</h3>
        <p className={`text-xl font-medium ${color}`}>{category}</p>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <SafeIcon icon={FiTarget} className="text-blue-500 mr-2" />
          BMI Range Guide
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Underweight:</span>
            <span className="font-medium">Below 18.5</span>
          </div>
          <div className="flex justify-between">
            <span>Normal weight:</span>
            <span className="font-medium">18.5 - 24.9</span>
          </div>
          <div className="flex justify-between">
            <span>Overweight:</span>
            <span className="font-medium">25.0 - 29.9</span>
          </div>
          <div className="flex justify-between">
            <span>Obese:</span>
            <span className="font-medium">30.0 and above</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-gray-800 mb-3">Health Tips</h4>
        <ul className="space-y-2">
          {tips.map((tip, index) => (
            <li key={index} className="flex items-start text-sm text-gray-600">
              <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

export default BMIResults;